import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ApiResponse } from '../types/api';
import { ChallengeConnection, ChallengePageInfo, CreatorChallenge } from '../types/challenges';

const SCROLL_THRESHOLD = 40;

type ChallengesFetcher = (after?: string | null) => Promise<ApiResponse<ChallengeConnection>>;

const useCursorChallenges = (fetcher: ChallengesFetcher, enabled = true) => {
  const [challenges, setChallenges] = useState<CreatorChallenge[]>([]);
  const [pageInfo, setPageInfo] = useState<ChallengePageInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(enabled);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const listRef = useRef<HTMLDivElement>(null);

  const fetchChallenges = async () => {
    setIsLoading(true);
    const response = await fetcher();
    if (response?.status) {
      setChallenges(response.data.edges.map(({ node }) => node));
      setPageInfo(response.data.pageInfo);
    } else {
      toast.error(response?.message || 'Something went wrong');
    }
    setIsLoading(false);
  };

  const fetchMoreChallenges = async () => {
    if (isLoadingMore || !pageInfo?.hasNextPage || !pageInfo.endCursor) return;
    setIsLoadingMore(true);
    const response = await fetcher(pageInfo.endCursor);
    if (response?.status) {
      setChallenges((prev) => [...prev, ...response.data.edges.map(({ node }) => node)]);
      setPageInfo(response.data.pageInfo);
    } else {
      toast.error(response?.message || 'Something went wrong');
    }
    setIsLoadingMore(false);
  };

  const handleScroll = () => {
    const list = listRef.current;
    if (!list) return;
    if (list.scrollTop + list.clientHeight >= list.scrollHeight - SCROLL_THRESHOLD) {
      fetchMoreChallenges();
    }
  };

  useEffect(() => {
    if (enabled) fetchChallenges();
  }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    challenges,
    isLoading,
    isLoadingMore,
    listRef,
    handleScroll,
    refetch: fetchChallenges,
  };
};

export default useCursorChallenges;
